import { forwardRef } from "react";

import { StackProps, Stack, useTheme } from "@mui/material";
import { getDollarValue, AvailablePaymentCurrency, formatNarrowCurrencyAmount } from "@simplyk/common";

import { useLocaleContext } from "@/contexts/LocaleContext";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";

type FormPaymentOrderItemProps = Omit<StackProps, "children"> & {
  label: string | React.ReactNode;
  price: number;
  currency: AvailablePaymentCurrency;
};

export const FormPaymentOrderItem = forwardRef<HTMLDivElement, FormPaymentOrderItemProps>(
  ({ label, price, currency, ...rest }, ref) => {
    const theme = useTheme();
    const { isoLocale } = useLocaleContext();

    if (!theme.palette.forms || !theme.palette.formsComponents?.paymentSummary) {
      return <InvalidThemeErrorMessage />;
    }

    return (
      <Stack
        ref={ref}
        direction="row"
        justifyContent="space-between"
        gap={2}
        data-test="order-ticket-information"
        {...rest}
      >
        <FormTypography variant="body2">{label}</FormTypography>
        <FormTypography variant="body2" sx={{ whiteSpace: "nowrap" }}>
          {formatNarrowCurrencyAmount(getDollarValue(price), { withCents: true, locale: isoLocale, currency })}
        </FormTypography>
      </Stack>
    );
  }
);

FormPaymentOrderItem.displayName = "FormPaymentOrderItem";
